/** Read-only spending projection. Bank evidence wins; the financial ledger is never mutated. */
import type { Clarification } from './clarifications';
export type SpendGroup = 'choices' | 'commitments' | 'review' | 'work';
export interface SpendRow {
  id: string; account: string; date: string; merchant: string;
  amountAed: number | null; original: number; currency: string;
  source: 'bank' | 'ledger' | 'sms'; certainty: 'bank' | 'recorded' | 'pending';
  category: string; group: SpendGroup; dateEstimated?: boolean; aliases?: string[]; clarification?: Clarification;
}
export interface Coverage { account: string; start: string; end: string }
export interface ReviewBaseline {
  asOf: string; rows: SpendRow[]; coverage: Coverage[];
  cashAed: number; monthlyRepeatAed: number;
  bills: Array<{ name: string; date: string; amount: number }>;
  notes: string[];
}
export interface Week {
  start: string; end: string; choices: number; commitments: number; review: number;
  work: number; bank: number; provisional: number; recorded: number; count: number;
  complete: boolean; currencyPending: number; dateEstimated: number;
  categories: Array<{ name: string; amount: number; group: SpendGroup }>;
  rows: SpendRow[];
}
export const HOUSEHOLD_CARDS = ['acc-enbd-cc', 'acc-ei-cc', 'acc-hsbc-cc', 'acc-citi-cc'];
const DAY = 86400000;
export function dubaiDay(iso: string): string {
  return new Date(new Date(iso).getTime() + 4 * 3600000).toISOString().slice(0, 10);
}
export function plusDays(day: string, n: number): string {
  return new Date(Date.parse(day + 'T12:00:00Z') + n * DAY).toISOString().slice(0, 10);
}
export function monday(day: string): string {
  const weekday = new Date(day + 'T12:00:00Z').getUTCDay();
  return plusDays(day, -((weekday + 6) % 7));
}
export const money = (n: number): number => Math.round(n * 100) / 100;

/** Dubai-local dates in SMS must not be interpreted as UTC and shifted into the next day. */
export function smsDay(raw: string, received: string): { date: string; dateEstimated: boolean } {
  const match = raw.match(/(?:Date:\s*|\bon\s+)(\d{2})\/(\d{2})\/(\d{4})/i);
  if (match) {
    const date = `${match[3]}-${match[2]}-${match[1]}`;
    if (Number.isFinite(Date.parse(date + 'T12:00:00Z')) && new Date(date + 'T12:00:00Z').toISOString().slice(0, 10) === date) return { date, dateEstimated: false };
  }
  return { date: dubaiDay(received), dateEstimated: true };
}
export function merchantKey(raw: string): string {
  if (/^UBER\s*\*?\s*(?:PENDING|TRIP)/i.test(raw)) return 'UBERRIDES';
  return raw.toUpperCase().replace(/^(?:NFC|IAP|POS|ECOM|MOTO)\s*-\s*\(AP-PAY\)-\s*/, '')
    .replace(/\s+(?:AE|ARE|IE|IRL|CA|CAN|US|USA|GB|GBR)$/, '').replace(/\s+(?:DUBAI|ABU\s?DHABI)$/, '')
    .replace(/[^A-Z0-9]/g, '');
}
function samePurchase(a: SpendRow, b: SpendRow): boolean {
  if (a.account !== b.account || Math.sign(a.original) !== Math.sign(b.original)) return false;
  const originalMatch = a.currency === b.currency && Math.round(a.original * 100) === Math.round(b.original * 100);
  const aedMatch = a.currency === 'AED' && b.currency === 'AED' && a.amountAed !== null && b.amountAed !== null && Math.round(a.amountAed * 100) === Math.round(b.amountAed * 100);
  if (!originalMatch && !aedMatch) return false;
  return Math.abs(Date.parse(a.date) - Date.parse(b.date)) <= 3 * DAY;
}
export function reconcileSpending(input: SpendRow[]): { rows: SpendRow[]; matched: number; ambiguous: SpendRow[] } {
  const ranks = { bank: 0, ledger: 1, sms: 2 };
  let rows: SpendRow[] = [];
  const ambiguous: SpendRow[] = [];
  let matched = 0;
  for (const source of ['bank', 'ledger', 'sms'] as const) {
    const incoming = input.map(r => ({ ...r, aliases: [...(r.aliases ?? [])] })).filter(r => r.source === source && HOUSEHOLD_CARDS.includes(r.account));
    const candidates = incoming.map(row => {
      const possible = rows.filter(other => ranks[other.source] < ranks[source] && samePurchase(row, other)
        && (!merchantKey(row.merchant) || !merchantKey(other.merchant) || merchantKey(row.merchant) === merchantKey(other.merchant)));
      const exactDate = row.dateEstimated ? [] : possible.filter(other => other.date === row.date);
      return exactDate.length ? exactDate : possible;
    });
    const counts = new Map<string, number>();
    for (const list of candidates) for (const r of list) counts.set(r.id, (counts.get(r.id) ?? 0) + 1);
    incoming.forEach((row, i) => {
      const list = candidates[i]!;
      const only = list[0];
      if (only && list.length === 1 && counts.get(only.id) === 1 && merchantKey(row.merchant) && merchantKey(row.merchant) === merchantKey(only.merchant)) {
        only.aliases = [...(only.aliases ?? []), row.id, ...(row.aliases ?? [])];
        matched++;
      } else if (list.length) {
        // Do not silently either sum or discard an uncertain duplicate. Keep it visible outside the total.
        ambiguous.push(row);
      } else rows.push(row);
    });
  }
  return { rows, matched, ambiguous };
}

export function categorize(merchant: string, category = '', fee = false): { category: string; group: SpendGroup } {
  const m = merchant.toUpperCase();
  if (fee || /PROCESSING FEE|BILLED PROFIT|VAT ON|SPEND NOT MET/.test(m)) return { category: 'Bank fees', group: 'commitments' };
  const rules: Array<[RegExp, string, SpendGroup]> = [
    [/REPTON|COGNITA/, 'School & activities', 'commitments'],
    [/MAMAS|EROS ELECTRICAL|TAMARA|TABBY|CRATE AND BARREL|KURTAINS|LOOM|DIGITAL FUTURE/, 'Purchases to check', 'review'],
    [/DELIVEROO|CAREEM FOOD/, 'Takeaways', 'choices'],
    [/ORGANIC FOODS|CHOITHRAM|ALL DAY MARKET|WAITROSE|INSTASHOP|AMAZON GROCERY|AL MAYA|NOON MINUTES|SPINNEYS/, 'Groceries', 'choices'],
    [/SEPHORA|ESSA BEAUTY|HAWAIIAN HUSH|LUSH |SALON|NAILS/, 'Personal care', 'choices'],
    [/JONES THE GROCER|CAFE|BBQ|THE LOUNGE|SHAKE SHACK|QLUB|RESTAURANT|PEETS|CHIPOTLE|ICE CREAM|STARBUCKS|MADO|PIPELINE|FIND SALT/, 'Eating out & coffee', 'choices'],
    [/UBER|UBR\*|^CAREEM (?!PLUS)/, 'Taxis & rides', 'choices'],
    [/EPPCO|ENOC /, 'Fuel & travel', 'choices'],
    [/THEME ?PARK|TOP GOLF|GREEN PLANET|BOO BOO|EMIRATES THE SEVENS/, 'Days out', 'choices'],
    [/WASHMEN|JUSTLIFE|CLEAN CAR/, 'Household services', 'choices'],
    [/AMAZON\.AE|AMZN MKTP/, 'Amazon purchases', 'choices'],
    [/GOBAREFOOT|OWNKIND|MUMZWORLD|SP SNOX|HENNES|FIVE LITTLE|VICTORIA LAURE/, 'Shopping', 'choices'],
    [/KINGS COLLEGE|KCH |HOSPITAL|GOLD LIFE|PHARMACY|BOOTS/, 'Health', 'commitments'],
    [/APPLE.COM|OPENAI|ANTHROPIC|APIFY|DIGITALOCEAN|NETFLIX|DISNEY|GOOGLE ONE|PRIME SUBSCRIPT|NOON ONE|CAREEM PLUS|DAZN|NATURALCYCLES/, 'Subscriptions', 'commitments'],
    [/DESERT BARBELL|BUILDANDBURN|WHOOP/, 'Fitness to check', 'review'],
    [/DUBAI ELECTRICITY|E& DIGITAL/, 'Utilities', 'commitments'],
  ];
  for (const [test, name, group] of rules) if (test.test(m)) return { category: name, group };
  const mappings: Record<string, string> = { 'cat-groceries': 'Groceries', 'cat-dining': 'Eating out & coffee', 'cat-beauty': 'Personal care', 'cat-shopping': 'Shopping', 'cat-entertainment': 'Days out', 'cat-transport': 'Fuel & travel' };
  if (mappings[category]) return { category: mappings[category]!, group: 'choices' };
  return { category: 'Needs a category', group: 'review' };
}

export function buildWeeks(rows: SpendRow[], coverage: Coverage[], today: string): Week[] {
  const thisMonday = monday(today);
  return Array.from({ length: 7 }, (_, i) => {
    const start = plusDays(thisMonday, (i - 6) * 7), end = plusDays(start, 6);
    const selected = rows.filter(r => r.date >= start && r.date <= end && r.date <= today);
    const cents = (rs: SpendRow[]) => money(rs.reduce((sum, r) => sum + (r.amountAed ?? 0), 0));
    const categories = new Map<string, { name: string; amount: number; group: SpendGroup }>();
    for (const row of selected) {
      const c = categories.get(row.category) ?? { name: row.category, amount: 0, group: row.group };
      c.amount = money(c.amount + (row.amountAed ?? 0)); categories.set(c.name, c);
    }
    return {
      start, end, choices: cents(selected.filter(r => r.group === 'choices')),
      commitments: cents(selected.filter(r => r.group === 'commitments')), review: cents(selected.filter(r => r.group === 'review')),
      work: cents(selected.filter(r => r.group === 'work')),
      bank: cents(selected.filter(r => r.certainty === 'bank')), provisional: cents(selected.filter(r => r.certainty === 'pending')),
      recorded: cents(selected.filter(r => r.certainty === 'recorded')), count: selected.length,
      complete: end < today && ['acc-enbd-cc', 'acc-ei-cc', 'acc-hsbc-cc'].every(account => coverage.some(c => c.account === account && c.start <= start && c.end >= end)),
      currencyPending: selected.filter(r => r.amountAed === null).length,
      dateEstimated: selected.filter(r => r.dateEstimated).length,
      categories: [...categories.values()].sort((a, b) => b.amount - a.amount), rows: selected.sort((a, b) => b.date.localeCompare(a.date)),
    };
  });
}
